import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtINR, fmtDate } from "@/lib/api";
import { AlertTriangle, Mail, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Overdue reminder preview + selective-send workflow.
 * Super_admin / admin only. Preview is loaded on-demand so the Dashboard
 * stays snappy on first paint.
 */
export default function OverdueRemindersButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);        // {candidates, generated_at, ...}
  const [picked, setPicked] = useState(new Set()); // keys the user wants to send
  const [sending, setSending] = useState(false);

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    setData(null);
    try {
      const r = await api.get("/admin/overdue-reminders-preview");
      setData(r.data);
      // Default-select every row that is actually sendable
      setPicked(new Set((r.data.candidates || []).filter(c => c.will_send).map(c => c.key)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
      setOpen(false);
    } finally { setLoading(false); }
  };

  const togglePick = (key) => {
    const next = new Set(picked);
    if (next.has(key)) next.delete(key); else next.add(key);
    setPicked(next);
  };

  const sendSelected = async () => {
    if (picked.size === 0) {
      toast.error("Select at least one reminder");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/overdue-reminders-send", { keys: Array.from(picked) });
      const parts = [`${r.data.sent_count} sent`];
      if (r.data.failed_count) parts.push(`${r.data.failed_count} failed`);
      if (r.data.skipped_count) parts.push(`${r.data.skipped_count} skipped`);
      toast.success(parts.join(" · "));
      // Refresh preview — rows that just sent will now show "already sent"
      const r2 = await api.get("/admin/overdue-reminders-preview");
      setData(r2.data);
      setPicked(new Set((r2.data.candidates || []).filter(c => c.will_send).map(c => c.key)));
      if (r.data.sent_count && !r.data.failed_count) {
        setTimeout(() => setOpen(false), 1200);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const sendable = (data?.candidates || []).filter(c => c.will_send);
  const skipped = (data?.candidates || []).filter(c => !c.will_send);

  return (
    <>
      <Button data-testid="overdue-preview-btn" variant="outline" onClick={openDialog}>
        <Mail className="h-4 w-4 mr-2"/>Overdue reminders
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!sending) setOpen(v); }}>
        <DialogContent data-testid="overdue-preview-dialog" className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Overdue reminder preview</DialogTitle>
            <DialogDescription>
              Reminders are generated for invoices that are exactly <strong>7, 15, or 30 days</strong> past their cycle end.
              Each invoice/window combo is only emailed once — rows already sent are shown but cannot be re-queued.
            </DialogDescription>
          </DialogHeader>

          {loading && <div className="py-8 text-center text-sm text-muted-foreground">Loading preview…</div>}

          {!loading && data && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Generated {fmtDate(data.generated_at)} · <strong className="text-foreground">{sendable.length}</strong> ready to send · <strong className="text-foreground">{skipped.length}</strong> skipped</span>
                {sendable.length > 0 && (
                  <div className="flex gap-2">
                    <Button data-testid="overdue-pick-all" size="sm" variant="ghost"
                      onClick={() => setPicked(new Set(sendable.map(c => c.key)))}>Select all</Button>
                    <Button data-testid="overdue-pick-none" size="sm" variant="ghost"
                      onClick={() => setPicked(new Set())}>Clear</Button>
                  </div>
                )}
              </div>

              {sendable.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  No overdue reminders are due to go out right now. Come back later when invoices cross a 7 / 15 / 30-day window.
                </div>
              ) : (
                <div className="max-h-96 overflow-auto border border-border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/60 sticky top-0">
                      <tr className="text-left">
                        <th className="px-3 py-2 w-10"></th>
                        <th className="px-3 py-2 font-heading text-xs uppercase tracking-wider">Invoice</th>
                        <th className="px-3 py-2 font-heading text-xs uppercase tracking-wider">Customer</th>
                        <th className="px-3 py-2 font-heading text-xs uppercase tracking-wider">Project</th>
                        <th className="px-3 py-2 font-heading text-xs uppercase tracking-wider text-right">Overdue</th>
                        <th className="px-3 py-2 font-heading text-xs uppercase tracking-wider text-right">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sendable.map((c) => (
                        <tr key={c.key} data-testid={`overdue-row-${c.key}`} className="border-t border-border hover:bg-muted/30">
                          <td className="px-3 py-2">
                            <Checkbox data-testid={`overdue-check-${c.key}`}
                              checked={picked.has(c.key)}
                              onCheckedChange={() => togglePick(c.key)}/>
                          </td>
                          <td className="px-3 py-2 tabular font-medium">{c.invoice_no}</td>
                          <td className="px-3 py-2">
                            <div>{c.customer_name || "-"}</div>
                            <div className="text-xs text-muted-foreground">{c.customer_email}</div>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{c.project_name || "-"}</td>
                          <td className="px-3 py-2 text-right tabular text-amber-700">{c.days_overdue}d</td>
                          <td className="px-3 py-2 text-right tabular font-medium text-destructive">{fmtINR(c.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {skipped.length > 0 && (
                <details className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                  <summary className="cursor-pointer text-muted-foreground flex items-center gap-1.5">
                    <AlertTriangle className="h-3 w-3"/>
                    {skipped.length} skipped (already-sent or no customer email)
                  </summary>
                  <div className="mt-2 space-y-1 tabular">
                    {skipped.map((c) => (
                      <div key={c.key} className="flex items-center justify-between gap-3">
                        <span className="truncate"><span className="font-medium">{c.invoice_no}</span> · {c.customer_name} ({c.days_overdue}d)</span>
                        <span className="text-muted-foreground shrink-0">{c.skip_reason}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={sending}>Close</Button>
            <Button data-testid="overdue-send-selected"
              onClick={sendSelected}
              disabled={sending || loading || picked.size === 0}>
              <Send className="h-4 w-4 mr-2"/>
              {sending ? "Sending…" : `Send ${picked.size} reminder${picked.size === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
